/**
 * Outcomes Monitoring Report — pure helpers (period math, axis framing, deltas).
 */

import { buildRecordsHref, type FilterConfig, type DateRangeFilterConfig } from '../../../records/filter';
import type { DetrimentMix, DetrimentType, Grain, SeriesPoint } from '../types';

/**
 * Link a detriment figure to the records queue, filtered to that dimension.
 *
 * A navigation aid — "take you to this queue" — not an exact reconstruction of the
 * figure's numerator. The rule is: **forward only what the collection-records
 * endpoint actually applies** (`list_records_in_collection`), which is the clicked
 * dimension and the date range. Specifically NOT forwarded:
 * - `conductScoreMin/Max` — the endpoint declares no such query params, so
 *   FastAPI drops them; a URL that showed the bounds would return records outside
 *   them (the report has no control to set them today either);
 * - `search` — the records page applies its own search box, not `filters.search`
 *   from the URL, so it is ignored;
 * - the report's existing dimension set — combining it with the clicked type is
 *   not expressible in the flat filter model.
 * The report's EFFECTIVE window is carried (pass `dateRange` = `filters.dateRange`),
 * including the injected 30-day default, so the records cohort matches the figure;
 * a cleared range carries the all-time sentinel and both surfaces agree. Lands on
 * the all-records queue (`queue=all`) so the page's default complaints queue does
 * not layer its own flag on top. All three types map exactly to the metric:
 * dissatisfaction uses the same `complaint_detected` predicate the mix counts
 * (records dimension filter aligned in filter_service), and strain/harm already did.
 */
export function buildDetrimentDrilldownHref(
  collectionId: string,
  type: DetrimentType,
  dateRange?: DateRangeFilterConfig,
): string {
  const config: FilterConfig = { dimensions: { values: [type], logic: 'or' } };
  if (dateRange) {
    config.dateRange = dateRange;
  }
  return buildRecordsHref(collectionId, config, 'all');
}

export const fmt = (n: number): string => Math.round(n).toLocaleString('en-GB');

/** "1 record" / "12 records" — correct singular/plural. */
export const recordsLabel = (n: number): string =>
  `${fmt(n)} ${Math.round(n) === 1 ? 'record' : 'records'}`;

const MINOR_WORDS = new Set(['and', 'or', 'of', 'the', 'to', 'for', 'in', 'a', 'an', 'on', 'with']);

/**
 * Tidy an outcome name for display:
 *  - drop a leading "Outcome N - " / "Outcome N:" prefix (TCF names carry one,
 *    e.g. "Outcome 1 - Fair Treatment" → "Fair Treatment"),
 *  - lower-case minor words that the source title-cased ("Suitable Products And
 *    Services" → "Suitable Products and Services"), never the first word.
 * Consumer Duty names have neither issue, so they pass through unchanged.
 */
export function cleanOutcomeName(name: string): string {
  const stripped = name.replace(/^\s*outcome\s+\d+\s*[-–—:.]\s*/i, '').trim() || name;
  return stripped
    .split(' ')
    .map((word, i) => (i > 0 && MINOR_WORDS.has(word.toLowerCase()) ? word.toLowerCase() : word))
    .join(' ');
}

// --- Deltas ------------------------------------------------------------------

export interface DeltaParts {
  direction: 'up' | 'down' | 'flat';
  magnitude: string;
  pct?: string;
}

/**
 * Delta of `cur` vs `prev`. Returns null when there is no comparison period
 * (prev == null) — the caller then renders nothing (SPEC §8: hide all deltas).
 */
export function computeDelta(
  cur: number,
  prev: number | null | undefined,
  opts: { dp?: number; pct?: boolean } = {},
): DeltaParts | null {
  if (prev === null || prev === undefined) return null;
  const diff = cur - prev;
  const direction: DeltaParts['direction'] = diff > 0 ? 'up' : diff < 0 ? 'down' : 'flat';
  const magnitude =
    opts.dp !== undefined ? Math.abs(diff).toFixed(opts.dp) : fmt(Math.abs(diff));
  const parts: DeltaParts = { direction, magnitude };
  if (opts.pct && prev !== 0) {
    parts.pct = `${(Math.abs(diff) / prev * 100).toFixed(1)}%`;
  }
  return parts;
}

// --- Period length + grain options -------------------------------------------

const DAY_MS = 86_400_000;

/** Days covered by the selected period (null = no explicit range → treat as wide). */
export function periodDays(filters: FilterConfig): number | null {
  const dr = filters.dateRange;
  if (!dr) return null;
  if (dr.preset === 'last_7_days') return 7;
  if (dr.preset === 'last_30_days') return 30;
  if (dr.preset === 'last_quarter') return 90;
  if (dr.from && dr.to) {
    const from = new Date(`${dr.from}T00:00:00Z`).getTime();
    const to = new Date(`${dr.to}T00:00:00Z`).getTime();
    if (!Number.isNaN(from) && !Number.isNaN(to) && to >= from) {
      return Math.round((to - from) / DAY_MS) + 1;
    }
  }
  return null;
}

export interface GrainOptions {
  allowed: Grain[];
  default: Grain;
}

/**
 * Grain choices adapt to the period so we never draw one bucket or 300 (SPEC
 * §6.2.1). Disallowed grains are disabled, not hidden, so the control keeps its
 * shape. A null period (all history) is treated as wide.
 */
export function grainOptionsFor(days: number | null): GrainOptions {
  if (days !== null && days <= 14) return { allowed: ['daily'], default: 'daily' };
  if (days !== null && days <= 90) return { allowed: ['daily', 'weekly'], default: 'weekly' };
  return { allowed: ['weekly', 'monthly'], default: 'weekly' };
}

// --- Axis framing ------------------------------------------------------------

/**
 * Conduct-score axis: top just above the band boundary in play, so a 0.3 move is
 * legible and the compressed scale is stated, never implied (SPEC §6.2.1).
 */
export function scoreAxis(scores: number[]): { edge: number; top: number; edgeLabel: string } {
  const peak = scores.length ? Math.max(...scores) : 0;
  // Mark the nearest meaningful band boundary: 3.6 for outcomes still in the low
  // band, otherwise the 6.6 high boundary. For high-scoring outcomes the line
  // sits *below* the data so you can see the scores are above the threshold —
  // never parked at the 10 ceiling, which marks nothing.
  const edge = peak < 3.6 ? 3.6 : 6.6;
  const top = Math.min(10, Math.max(peak, edge) + 1.5);
  const edgeLabel = edge === 3.6 ? '3.6 — moderate band' : '6.6 — high band';
  return { edge, top, edgeLabel };
}

/**
 * Detriment share axis: framed on the observed range, not 0–100%, so three
 * shares clustered near a third each don't render as a flat tangle (SPEC §6.2.1).
 */
export function shareAxis(shares: number[]): { lo: number; hi: number } {
  if (!shares.length) return { lo: 0, hi: 100 };
  let lo = Math.max(0, Math.floor(Math.min(...shares) / 5) * 5 - 5);
  let hi = Math.min(100, Math.ceil(Math.max(...shares) / 5) * 5 + 5);
  if (hi - lo < 20) {
    lo = Math.max(0, lo - 5);
    hi = Math.min(100, hi + 5);
  }
  return { lo, hi };
}

/** Per-bucket detriment shares (%) of the three, summing to 100 per bucket. */
export function bucketShares(d: DetrimentMix): [number, number, number] {
  const total = d.dissatisfaction + d.strain + d.harm || 1;
  return [
    (d.dissatisfaction / total) * 100,
    (d.strain / total) * 100,
    (d.harm / total) * 100,
  ];
}

/** Chart title suffix following the selected period (e.g. "— Last quarter"). */
export function periodLabel(filters: FilterConfig): string {
  const dr = filters.dateRange;
  if (!dr) return 'All time';
  if (dr.preset === 'last_7_days') return 'Last 7 days';
  if (dr.preset === 'last_30_days') return 'Last 30 days';
  if (dr.preset === 'last_quarter') return 'Last quarter';
  if (dr.from && dr.to) return `${dr.from} – ${dr.to}`;
  return 'All time';
}

/** Whole-period detriment counts = sum of per-bucket counts (each record is in one bucket). */
export function sumDetriment(series: SeriesPoint[]): DetrimentMix {
  return series.reduce<DetrimentMix>(
    (acc, p) => ({
      dissatisfaction: acc.dissatisfaction + p.detriment.dissatisfaction,
      strain: acc.strain + p.detriment.strain,
      harm: acc.harm + p.detriment.harm,
    }),
    { dissatisfaction: 0, strain: 0, harm: 0 },
  );
}
